import axios from '@/axios';

/**
 * QR코드 관리 API
 */
export const QrApi = {
  /**
   * QR코드 목록 조회
   * @param {Object} params - 검색 파라미터
   * @param {number} [params.page] - 페이지 번호
   * @param {number} [params.limit] - 페이지 크기
   * @param {string} [params.name] - QR코드명 검색어
   * @param {string} [params.categoryId] - 카테고리 필터
   * @param {string} [params.status] - 상태 필터 (active/inactive)
   * @param {string} [params.tag] - 태그 필터
   * @returns {Promise}
   */
  getQrList: async (params = {}) => {
    return await axios.get('/qr-codes', { params });
  },

  /**
   * QR코드 상세 조회
   * @param {string} qrId - QR코드 ID
   * @returns {Promise}
   */
  getQr: async (qrId) => {
    return await axios.get(`/qr-codes/${qrId}`);
  },

  /**
   * QR코드 생성
   * @param {Object} qrData - QR코드 데이터
   * @param {string} qrData.name - QR코드명
   * @param {string} [qrData.description] - 설명
   * @param {string} qrData.targetUrl - 목적지 URL
   * @param {string} [qrData.categoryId] - 카테고리 ID
   * @param {Array<string>} [qrData.tags] - 태그 목록
   * @returns {Promise}
   */
  createQr: async (qrData) => {
    return await axios.post('/qr-codes', qrData);
  },

  /**
   * QR코드 수정
   * @param {string} qrId - QR코드 ID
   * @param {Object} qrData - QR코드 데이터
   * @returns {Promise}
   */
  updateQr: async (qrId, qrData) => {
    return await axios.put(`/qr-codes/${qrId}`, qrData);
  },

  /**
   * QR코드 삭제
   * @param {string} qrId - QR코드 ID
   * @returns {Promise}
   */
  deleteQr: async (qrId) => {
    return await axios.delete(`/qr-codes/${qrId}`);
  },

  /**
   * QR코드 목적지 URL 변경
   * @param {string} qrId - QR코드 ID
   * @param {string} targetUrl - 새 목적지 URL
   * @returns {Promise}
   */
  updateTargetUrl: async (qrId, targetUrl) => {
    return await axios.patch(`/qr-codes/${qrId}/target-url`, { targetUrl });
  },

  /**
   * QR코드 URL 변경 이력 조회
   * @param {string} qrId - QR코드 ID
   * @param {Object} [params] - 페이지네이션 파라미터
   * @returns {Promise}
   */
  getUrlHistory: async (qrId, params = {}) => {
    return await axios.get(`/qr-codes/${qrId}/url-history`, { params });
  },

  /**
   * QR코드 URL 복원 (이전 URL로 되돌리기)
   * @param {string} qrId - QR코드 ID
   * @param {string} historyId - 이력 ID
   * @returns {Promise}
   */
  restoreUrl: async (qrId, historyId) => {
    return await axios.post(`/qr-codes/${qrId}/url-history/${historyId}/restore`);
  },

  /**
   * QR코드 스캔 통계 조회 (다중 QR, KST 버킷) — /qr/stats 탐색 페이지 전용 계약
   * 400: 서버 가드(기간·단위·QR 개수 초과) 위반, 한국어 message 포함. 404: 선택된 QR코드를 모두 찾을 수 없음(페이지 not-found).
   * 개발 픽스처는 VUE_APP_QR_STATS_FIXTURE=1일 때만 사용한다(404 대체 폴백 아님).
   * 응답: { buckets:[{key,label,series:{[qrId]:{scans,unique}}}],
   *         summary:{[qrId]:{totalScans,uniqueVisitors,todayScans,peakHour,name,shortCode,createdAt}},
   *         breakdown:{device|browser|os|referer:[{name,count,pct}]},
   *         notFound:string[] — 일부 QR코드만 찾을 수 없을 때 제외된 id 목록, tz, granularity, view }
   * @param {Object} params
   * @param {string} params.qrIds - 쉼표로 이은 QR코드 ID 목록 (최대 5개)
   * @param {string} params.from - 시작일 YYYY-MM-DD (KST)
   * @param {string} params.to - 종료일 YYYY-MM-DD (KST)
   * @param {string} params.granularity - minute|hour|day|week|month
   * @param {string} params.view - timeline|hourOfDay|dayOfWeek|breakdown
   * @param {string} params.metric - scans|unique
   * @param {string} [params.tz='Asia/Seoul'] - 버킷 기준 시간대
   * @returns {Promise}
   */
  getMultiStats: async (params = {}) => {
    return await axios.get('/qr-codes/stats', { params: { tz: 'Asia/Seoul', ...params } });
  },

  /**
   * QR코드 상태 변경 (활성/비활성)
   * @param {string} qrId - QR코드 ID
   * @param {string} status - 상태 (active/inactive)
   * @returns {Promise}
   */
  updateStatus: async (qrId, status) => {
    return await axios.patch(`/qr-codes/${qrId}/status`, { status });
  },

  /**
   * 전체 QR코드 통계 대시보드 조회
   * @param {Object} [params] - 조회 파라미터
   * @returns {Promise}
   */
  getDashboardStats: async (params = {}) => {
    return await axios.get('/qr-codes/dashboard/stats', { params });
  },

  /**
   * QR코드 대량 생성
   * @param {Object} bulkData - 대량 생성 데이터
   * @param {Array} bulkData.items - QR코드 항목 배열
   * @param {Object} [bulkData.options] - 생성 옵션
   * @returns {Promise}
   */
  bulkCreate: async (bulkData) => {
    return await axios.post('/qr-codes/bulk', bulkData);
  },

  /**
   * QR코드 대량 생성 검증 (미리보기)
   * @param {Object} bulkData - 대량 생성 데이터
   * @returns {Promise}
   */
  bulkValidate: async (bulkData) => {
    return await axios.post('/qr-codes/bulk/validate', bulkData);
  },
};

/**
 * QR 카테고리 API
 */
export const QrCategoryApi = {
  /**
   * 카테고리 목록 조회
   * @returns {Promise}
   */
  getCategories: async () => {
    return await axios.get('/qr-categories');
  },

  /**
   * 카테고리 생성
   * @param {Object} categoryData - 카테고리 데이터
   * @returns {Promise}
   */
  createCategory: async (categoryData) => {
    return await axios.post('/qr-categories', categoryData);
  },

  /**
   * 카테고리 수정
   * @param {string} categoryId - 카테고리 ID
   * @param {Object} categoryData - 카테고리 데이터
   * @returns {Promise}
   */
  updateCategory: async (categoryId, categoryData) => {
    return await axios.put(`/qr-categories/${categoryId}`, categoryData);
  },

  /**
   * 카테고리 삭제
   * @param {string} categoryId - 카테고리 ID
   * @returns {Promise}
   */
  deleteCategory: async (categoryId) => {
    return await axios.delete(`/qr-categories/${categoryId}`);
  },
};

/**
 * QR 태그 API
 */
export const QrTagApi = {
  /**
   * 태그 목록 조회
   * @returns {Promise}
   */
  getTags: async () => {
    return await axios.get('/qr-tags');
  },

  /**
   * 태그 생성
   * @param {Object} tagData - 태그 데이터
   * @returns {Promise}
   */
  createTag: async (tagData) => {
    return await axios.post('/qr-tags', tagData);
  },

  /**
   * 태그 수정
   * @param {string} tagId - 태그 ID
   * @param {Object} tagData - 태그 데이터
   * @returns {Promise}
   */
  updateTag: async (tagId, tagData) => {
    return await axios.put(`/qr-tags/${tagId}`, tagData);
  },

  /**
   * 태그 삭제
   * @param {string} tagId - 태그 ID
   * @returns {Promise}
   */
  deleteTag: async (tagId) => {
    return await axios.delete(`/qr-tags/${tagId}`);
  },
};
